/**
 * @file        loading.interceptor.ts
 * @description Shows the shared loading overlay while API calls run
 * @version     1.0.0
 * @author      Boris Veits
 * @copyright   Copyright (c) 2018, MindShare HDV LLC.  All rights reserved.
 *              Property of MindShare HDV LLC.  This file cannot be used or
 *              altered in anyway without expressed written permission from
 *              MindShare HDV LLC.
 */
import { Injectable } from '@angular/core';
import
{
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest
}
    from '@angular/common/http';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { environment } from '@environments/environment';
import
{
    LOADING_SKIP_URL_PARTS,
    SKIP_LOADING
}
    from '@app/constants/Loading.constant';
import { LoadingService } from '@app/service/loading.service';

@Injectable()
class LoadingInterceptor implements HttpInterceptor
{
    /**
     * @desc   - inject the shared loading state
     * @access - public
     * @param  - loadingSrv : pending work tracker
     * @return -
     */
    constructor
    (
        private loadingSrv: LoadingService
    )
    {
    }

    /**
     * @desc   - whether a request should drive the overlay
     * @access - private
     * @param  - HttpRequest request : outgoing request
     * @return - boolean : true when the overlay should track it
     */
    private isTracked
    (
        request: HttpRequest<object>
    ): boolean
    {
        let result: boolean;
        let isApiUrl: boolean;
        let isSkipped: boolean;

        isApiUrl = request.url.startsWith(environment.apiUrl);
        isSkipped = request.context.get(SKIP_LOADING) ||
            LOADING_SKIP_URL_PARTS.some((part: string) =>
            {
                return request.url.includes(part);
            });
        result = isApiUrl && !isSkipped;

        return result;
    }

    /**
     * @desc   - count tracked API calls in the loading service
     * @access - public
     * @param  - HttpRequest request : outgoing request
     * @param  - HttpHandler next : next handler in the chain
     * @return - Observable : HTTP event stream
     */
    intercept
    (
        request: HttpRequest<object>,
        next: HttpHandler
    ): Observable<HttpEvent<object>>
    {
        let result: Observable<HttpEvent<object>>;

        if (this.isTracked(request))
        {
            this.loadingSrv.show();
            result = next.handle(request).pipe(finalize(() =>
            {
                this.loadingSrv.hide();
            }));
        }
        else
        {
            result = next.handle(request);
        }

        return result;
    }
}

export { LoadingInterceptor };
